import React, { useState, useMemo, useCallback, useRef } from "react";
import { cn } from "./utils/cn";
import { getToothShape, SURFACE_LABELS, ODONTOGRAM_COLORS, CLINICAL_FINDING_DEFS, DECIDUOUS_SLOT_MAP } from "./utils/toothChartUtils";
import CurveToothSVG from "./CurveToothSVG";
import { useToothSurfaceSelection } from "./useToothSurfaceSelection";
import { Check, Trash2 } from "lucide-react";

/* ---------------------------------------------------------
   Interactive Tooth Chart -- Curve-style dual anatomical views
   Layout (top -> bottom):
     Upper buccal view
     Upper lingual view
     --- Tooth numbers ---
     Lower lingual view
     Lower buccal view
   --------------------------------------------------------- */
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
/* -- Constants -- */
const UPPER = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16];
const LOWER = [32, 31, 30, 29, 28, 27, 26, 25, 24, 23, 22, 21, 20, 19, 18, 17];
const PRIMARY_UPPER = [4, 5, 6, 7, 8, 9, 10, 11, 12, 13];
const PRIMARY_LOWER = [29, 28, 27, 26, 25, 24, 23, 22, 21, 20];
const SLOT_TO_LETTER = {};
for (const [letter, slot] of Object.entries(DECIDUOUS_SLOT_MAP)) {
  SLOT_TO_LETTER[slot] = letter;
}
const PRIMARY_SLOTS = new Set(Object.values(DECIDUOUS_SLOT_MAP));
function getTeethArrays(mode) {
  if (mode === "primary") return {
    upper: PRIMARY_UPPER,
    lower: PRIMARY_LOWER
  };
  return {
    upper: UPPER,
    lower: LOWER
  };
}
function getToothLabel(toothNumber, mode) {
  if (mode === "primary") return SLOT_TO_LETTER[toothNumber] ?? String(toothNumber);
  if (mode === "mixed" && SLOT_TO_LETTER[toothNumber]) {
    return `${SLOT_TO_LETTER[toothNumber]}`;
  }
  return String(toothNumber);
}
const CONDITION_LABELS = {
  present: "Present",
  missing: "Missing",
  crown: "Crown",
  implant: "Implant",
  root_canal: "Root Canal",
  impacted: "Impacted",
  supernumerary: "Supernumerary",
  pontic: "Pontic"
};
const CONDITION_COLORS = {
  present: "bg-emerald-50 text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-400",
  missing: "bg-gray-100 text-gray-500",
  crown: "bg-purple-50 text-purple-700 dark:bg-purple-900/20 dark:text-purple-400",
  implant: "bg-cyan-50 text-cyan-700 dark:bg-cyan-900/20 dark:text-cyan-400",
  root_canal: "bg-amber-50 text-amber-700 dark:bg-amber-900/20 dark:text-amber-400",
  impacted: "bg-violet-50 text-violet-700 dark:bg-violet-900/20 dark:text-violet-400",
  supernumerary: "bg-pink-50 text-pink-700 dark:bg-pink-900/20 dark:text-pink-400",
  pontic: "bg-gray-100 text-gray-600 dark:bg-neutral-800 dark:text-neutral-400"
};

/* -- Tooth Column -- */
const ToothColumn = /*#__PURE__*/React.memo(function ToothColumn({
  tooth,
  upper,
  hovered,
  selected,
  cellWidth,
  toothWidth,
  primaryToothHeight,
  secondaryToothHeight,
  onHover,
  onSelect,
  onContextMenu: onCtxMenu,
  selectedSurfaces,
  onSurfaceToggle,
  hoveredSurface,
  onSurfaceHover,
  enhancedState,
  layerVisibility,
  dimmed,
  label,
  isPrimaryTooth
}) {
  const n = tooth.toothNumber;
  const handleClick = useCallback(() => {
    onSelect(selected ? null : n);
  }, [n, selected, onSelect]);
  const handleContextMenu = useCallback(e => {
    e.preventDefault();
    onSelect(n);
    onCtxMenu?.(e, n);
  }, [n, onSelect, onCtxMenu]);
  const handleEnter = useCallback(() => onHover(n), [n, onHover]);
  const handleLeave = useCallback(() => onHover(null), [onHover]);
  const handleSurfaceToggle = useCallback(surface => {
    onSelect(n);
    onSurfaceToggle?.(n, surface);
  }, [n, onSelect, onSurfaceToggle]);
  const handleSurfaceHover = useCallback(surface => onSurfaceHover?.(n, surface), [n, onSurfaceHover]);
  const buccalHeight = upper ? primaryToothHeight : secondaryToothHeight;
  const lingualHeight = upper ? secondaryToothHeight : primaryToothHeight;
  const buccalView = /*#__PURE__*/_jsx("div", {
    className: "flex items-center justify-center",
    style: {
      width: cellWidth,
      height: buccalHeight
    },
    children: /*#__PURE__*/_jsx(CurveToothSVG, {
      tooth: tooth,
      selected: selected,
      onSelect: handleClick,
      view: "buccal",
      upper: upper,
      width: toothWidth,
      height: buccalHeight,
      selectedSurfaces: selectedSurfaces,
      onSurfaceToggle: handleSurfaceToggle,
      hoveredSurface: hoveredSurface,
      onSurfaceHover: handleSurfaceHover,
      enhancedState: enhancedState,
      layerVisibility: layerVisibility
    })
  });
  const lingualView = /*#__PURE__*/_jsx("div", {
    className: "flex items-center justify-center",
    style: {
      width: cellWidth,
      height: lingualHeight
    },
    children: /*#__PURE__*/_jsx(CurveToothSVG, {
      tooth: tooth,
      selected: selected,
      onSelect: handleClick,
      view: "lingual",
      upper: upper,
      width: toothWidth,
      height: lingualHeight,
      selectedSurfaces: selectedSurfaces,
      onSurfaceToggle: handleSurfaceToggle,
      hoveredSurface: hoveredSurface,
      onSurfaceHover: handleSurfaceHover,
      enhancedState: enhancedState,
      layerVisibility: layerVisibility
    })
  });

  /* Hover highlight wrapper */
  const highlight = hovered || selected;
  return /*#__PURE__*/_jsxs("div", {
    className: cn("flex flex-col items-center relative transition-all duration-150 rounded-lg", highlight && "z-10", dimmed && !selected && !hovered && "opacity-20"),
    style: {
      width: cellWidth
    },
    onMouseEnter: handleEnter,
    onMouseLeave: handleLeave,
    onContextMenu: handleContextMenu,
    children: [highlight && /*#__PURE__*/_jsx("div", {
      className: cn("absolute inset-0 rounded-lg transition-colors duration-75 pointer-events-none", selected ? "bg-blue-100/70 dark:bg-blue-900/30 ring-1 ring-blue-300 dark:ring-blue-700" : "bg-blue-50/60 dark:bg-blue-900/20")
    }), /*#__PURE__*/_jsxs("div", {
      className: "relative flex flex-col items-center gap-0.5",
      children: [upper && /*#__PURE__*/_jsx("div", {
        className: cn("text-[9px] font-semibold leading-none transition-colors duration-75 cursor-pointer", isPrimaryTooth ? "text-purple-600 dark:text-purple-400" : "", selected ? "text-blue-600 dark:text-blue-400" : !isPrimaryTooth ? "text-gray-500 dark:text-neutral-400" : ""),
        onClick: handleClick,
        children: label ?? n
      }), upper ? /*#__PURE__*/_jsxs(_Fragment, {
        children: [buccalView, lingualView]
      }) : /*#__PURE__*/_jsxs(_Fragment, {
        children: [lingualView, buccalView]
      }), !upper && /*#__PURE__*/_jsx("div", {
        className: cn("text-[9px] font-semibold leading-none transition-colors duration-75 cursor-pointer", isPrimaryTooth ? "text-purple-600 dark:text-purple-400" : "", selected ? "text-blue-600 dark:text-blue-400" : !isPrimaryTooth ? "text-gray-500 dark:text-neutral-400" : ""),
        onClick: handleClick,
        children: label ?? n
      })]
    })]
  });
});

/* -- Number strip between arches -- */
function NumberStrip({
  upper,
  lower,
  teethMap,
  hoveredTooth,
  selectedTooth,
  getCellWidth,
  expanded,
  onHover,
  onSelect,
  dentitionMode = "adult"
}) {
  const fontSize = expanded ? "text-xs" : "text-[10px]";
  const rowH = expanded ? "h-7" : "h-5";
  const missingSize = expanded ? "text-[10px]" : "text-[8px]";
  const renderRow = nums => {
    const mid = Math.ceil(nums.length / 2);
    return /*#__PURE__*/_jsxs("div", {
      className: "flex justify-center",
      children: [/*#__PURE__*/_jsx("div", {
        className: "flex",
        children: nums.slice(0, mid).map(n => {
          const active = hoveredTooth === n || selectedTooth === n;
          const isMissing = teethMap.get(n)?.condition === "missing";
          const lbl = getToothLabel(n, dentitionMode);
          const isPrimary = dentitionMode === "mixed" && PRIMARY_SLOTS.has(n);
          return /*#__PURE__*/_jsxs("div", {
            className: cn("flex items-center justify-center font-bold cursor-pointer transition-colors duration-75 border border-sky-100/80 dark:border-neutral-700 bg-sky-50/70 dark:bg-blue-950/20", fontSize, rowH, active ? "text-blue-600 dark:text-blue-400" : isMissing ? "text-gray-300" : isPrimary ? "text-purple-600 dark:text-purple-400" : "text-gray-500 dark:text-neutral-400"),
            style: {
              width: getCellWidth(n)
            },
            onMouseEnter: () => onHover(n),
            onMouseLeave: () => onHover(null),
            onClick: () => onSelect(selectedTooth === n ? null : n),
            children: [lbl, isMissing && /*#__PURE__*/_jsx("span", {
              className: cn(missingSize, "text-gray-300 ml-px"),
              children: "X"
            })]
          }, n);
        })
      }), /*#__PURE__*/_jsx("div", {
        className: "w-px bg-gray-200 dark:bg-neutral-700 mx-0.5 self-stretch"
      }), /*#__PURE__*/_jsx("div", {
        className: "flex",
        children: nums.slice(mid).map(n => {
          const active = hoveredTooth === n || selectedTooth === n;
          const isMissing = teethMap.get(n)?.condition === "missing";
          const lbl = getToothLabel(n, dentitionMode);
          const isPrimary = dentitionMode === "mixed" && PRIMARY_SLOTS.has(n);
          return /*#__PURE__*/_jsxs("div", {
            className: cn("flex items-center justify-center font-bold cursor-pointer transition-colors duration-75 border border-sky-100/80 dark:border-neutral-700 bg-sky-50/70 dark:bg-blue-950/20", fontSize, rowH, active ? "text-blue-600 dark:text-blue-400" : isMissing ? "text-gray-300" : isPrimary ? "text-purple-600 dark:text-purple-400" : "text-gray-500 dark:text-neutral-400"),
            style: {
              width: getCellWidth(n)
            },
            onMouseEnter: () => onHover(n),
            onMouseLeave: () => onHover(null),
            onClick: () => onSelect(selectedTooth === n ? null : n),
            children: [lbl, isMissing && /*#__PURE__*/_jsx("span", {
              className: cn(missingSize, "text-gray-300 ml-px"),
              children: "X"
            })]
          }, n);
        })
      })]
    });
  };
  return /*#__PURE__*/_jsxs("div", {
    className: "flex flex-col border-y border-gray-100 dark:border-neutral-700/50 py-0.5 gap-0.5 bg-blue-50/20 dark:bg-blue-950/10",
    children: [renderRow(upper), renderRow(lower)]
  });
}

/* -- Detail panel -- */

function ToothDetailPanel({
  tooth,
  selectedSurfaces,
  onClearSurfaces,
  procedures,
  onProcedureStatusChange,
  onProcedureDelete
}) {
  if (!tooth) {
    return /*#__PURE__*/_jsx("div", {
      className: "flex items-center justify-center h-full text-gray-400 text-xs italic py-8",
      children: "Hover or click a tooth"
    });
  }
  const shape = getToothShape(tooth.toothNumber);
  const hasSurfaces = selectedSurfaces && selectedSurfaces.length > 0;
  return /*#__PURE__*/_jsxs("div", {
    className: "p-3 space-y-2.5",
    children: [/*#__PURE__*/_jsxs("div", {
      className: "flex items-center gap-2.5",
      children: [/*#__PURE__*/_jsx("div", {
        className: "w-9 h-9 rounded-lg bg-blue-50 dark:bg-blue-900/20 flex items-center justify-center text-base font-bold text-blue-600",
        children: tooth.toothNumber
      }), /*#__PURE__*/_jsxs("div", {
        className: "flex-1 min-w-0",
        children: [/*#__PURE__*/_jsxs("div", {
          className: "text-sm font-semibold text-gray-800 dark:text-neutral-200",
          children: ["Tooth #", tooth.toothNumber]
        }), /*#__PURE__*/_jsxs("div", {
          className: "text-[10px] text-gray-500 capitalize",
          children: [shape, " \xB7", " ", /*#__PURE__*/_jsx("span", {
            className: cn("px-1.5 py-0.5 rounded-md text-[9px] font-medium", CONDITION_COLORS[tooth.condition] ?? ""),
            children: CONDITION_LABELS[tooth.condition] ?? tooth.condition
          })]
        })]
      })]
    }), tooth.surfaces && tooth.surfaces.length > 0 && /*#__PURE__*/_jsxs("div", {
      className: "space-y-1 pt-1",
      children: [/*#__PURE__*/_jsx("div", {
        className: "text-[9px] font-bold uppercase tracking-wider text-gray-400",
        children: "Surfaces"
      }), tooth.surfaces.map(s => {
        const color = s.planned ? "bg-blue-500" : s.condition === "caries" ? "bg-red-500" : s.restoration ? "bg-green-500" : "bg-gray-300";
        const label = s.planned ? "Planned" : s.condition === "caries" ? "Caries" : s.restoration ? s.restoration.charAt(0).toUpperCase() + s.restoration.slice(1) : "Healthy";
        return /*#__PURE__*/_jsxs("div", {
          className: "flex items-center gap-2 text-[11px]",
          children: [/*#__PURE__*/_jsx("span", {
            className: cn("w-2 h-2 rounded-full shrink-0", color)
          }), /*#__PURE__*/_jsx("span", {
            className: "font-medium text-gray-700 dark:text-neutral-300 w-12",
            children: SURFACE_LABELS[s.surface] ?? s.surface
          }), /*#__PURE__*/_jsx("span", {
            className: "text-gray-400 capitalize",
            children: label
          })]
        }, s.surface);
      })]
    }), hasSurfaces && /*#__PURE__*/_jsxs("div", {
      className: "space-y-1 pt-1",
      children: [/*#__PURE__*/_jsxs("div", {
        className: "flex items-center justify-between",
        children: [/*#__PURE__*/_jsx("div", {
          className: "text-[9px] font-bold uppercase tracking-wider text-blue-500",
          children: "Selected Surfaces"
        }), /*#__PURE__*/_jsx("button", {
          type: "button",
          onClick: onClearSurfaces,
          className: "text-[10px] text-gray-400 hover:text-gray-600 transition-colors",
          children: "Clear"
        })]
      }), /*#__PURE__*/_jsx("div", {
        className: "flex flex-wrap gap-1",
        children: selectedSurfaces.map(s => /*#__PURE__*/_jsx("span", {
          className: "px-1.5 py-0.5 rounded text-[10px] font-medium bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
          children: SURFACE_LABELS[s] ?? s
        }, s))
      })]
    }), tooth.findings && tooth.findings.length > 0 && /*#__PURE__*/_jsxs("div", {
      className: "space-y-1 pt-1",
      children: [/*#__PURE__*/_jsx("div", {
        className: "text-[9px] font-bold uppercase tracking-wider text-gray-400",
        children: "Findings"
      }), tooth.findings.map(f => {
        const def = CLINICAL_FINDING_DEFS.find(d => d.type === f.type);
        return /*#__PURE__*/_jsxs("div", {
          className: "flex items-center gap-2 text-[11px]",
          children: [/*#__PURE__*/_jsx("span", {
            className: "w-2 h-2 rounded-full shrink-0",
            style: {
              backgroundColor: def?.color ?? "#999"
            }
          }), /*#__PURE__*/_jsx("span", {
            className: "font-medium text-gray-700 dark:text-neutral-300",
            children: def?.shortLabel ?? f.type
          }), f.surfaces && f.surfaces.length > 0 && /*#__PURE__*/_jsx("span", {
            className: "text-[9px] text-gray-400",
            children: f.surfaces.join(",")
          }), /*#__PURE__*/_jsx("span", {
            className: cn("text-[9px] px-1 rounded", f.status === "active" ? "bg-red-50 text-red-600" : "bg-gray-100 text-gray-500"),
            children: f.status
          })]
        }, f.id);
      })]
    }), tooth.existingRestorations && tooth.existingRestorations.length > 0 && /*#__PURE__*/_jsxs("div", {
      className: "space-y-1 pt-1",
      children: [/*#__PURE__*/_jsx("div", {
        className: "text-[9px] font-bold uppercase tracking-wider text-gray-400",
        children: "Existing Restorations"
      }), tooth.existingRestorations.map(r => /*#__PURE__*/_jsxs("div", {
        className: "flex items-center gap-2 text-[11px]",
        children: [/*#__PURE__*/_jsx("span", {
          className: "w-2 h-2 rounded-full shrink-0 bg-emerald-500"
        }), /*#__PURE__*/_jsx("span", {
          className: "font-medium text-gray-700 dark:text-neutral-300 capitalize",
          children: r.type.replace("_", " ")
        }), r.surfaces && r.surfaces.length > 0 && /*#__PURE__*/_jsx("span", {
          className: "text-[9px] text-gray-400",
          children: r.surfaces.join(",")
        })]
      }, r.id))]
    }), procedures && procedures.length > 0 && /*#__PURE__*/_jsxs("div", {
      className: "space-y-0.5 pt-1",
      children: [/*#__PURE__*/_jsxs("div", {
        className: "text-[9px] font-bold uppercase tracking-wider text-gray-400",
        children: ["Procedures (", procedures.length, ")"]
      }), procedures.map(p => {
        const statusColor = p.status === "TP" ? "bg-red-500" : p.status === "C" ? "bg-green-500" : p.status === "EC" || p.status === "EO" ? "bg-blue-400" : p.status === "REFERRED" ? "bg-purple-500" : "bg-gray-400";
        const statusLabel = p.status === "TP" ? "Planned" : p.status === "C" ? "Completed" : p.status === "EC" || p.status === "EO" ? "Existing" : p.status === "REFERRED" ? "Referred" : p.status === "DECLINED" ? "Declined" : p.status;
        const hasActions = !!onProcedureStatusChange;
        const rowContent = /*#__PURE__*/_jsxs("div", {
          className: "flex items-start gap-2 text-[11px] w-full",
          children: [/*#__PURE__*/_jsx("span", {
            className: cn("w-2 h-2 rounded-full shrink-0 mt-0.5", statusColor)
          }), /*#__PURE__*/_jsxs("div", {
            className: "flex-1 min-w-0",
            children: [/*#__PURE__*/_jsxs("div", {
              className: "font-medium text-gray-700 dark:text-neutral-300 truncate",
              children: [p.cdtCode, " \u2014 ", p.description]
            }), /*#__PURE__*/_jsxs("div", {
              className: "flex items-center gap-1.5 text-[9px] text-gray-400",
              children: [/*#__PURE__*/_jsx("span", {
                children: statusLabel
              }), p.feeCents > 0 && /*#__PURE__*/_jsxs("span", {
                children: ["\xB7 $", (p.feeCents / 100).toFixed(0)]
              })]
            })]
          })]
        });
        if (!hasActions) {
          return /*#__PURE__*/_jsx("div", {
            className: "py-1",
            children: rowContent
          }, p.id);
        }
        return /*#__PURE__*/_jsxs("div", {
          className: "flex items-center gap-1 py-1",
          children: [/*#__PURE__*/_jsx("div", {
            className: "flex-1 min-w-0",
            children: rowContent
          }), p.status !== "C" && onProcedureStatusChange && /*#__PURE__*/_jsx("button", {
            type: "button",
            className: "p-1 rounded hover:bg-gray-100 dark:hover:bg-neutral-800 text-green-600",
            title: "Mark Completed",
            onClick: () => onProcedureStatusChange(p.id, "C"),
            children: /*#__PURE__*/_jsx(Check, {
              className: "h-3 w-3"
            })
          }), onProcedureDelete && /*#__PURE__*/_jsx("button", {
            type: "button",
            className: "p-1 rounded hover:bg-gray-100 dark:hover:bg-neutral-800 text-red-600",
            title: "Delete",
            onClick: () => onProcedureDelete(p.id),
            children: /*#__PURE__*/_jsx(Trash2, {
              className: "h-3 w-3"
            })
          })]
        }, p.id);
      })]
    })]
  });
}

/* -- Legend -- */
function ChartLegend() {
  const items = [{
    label: "Healthy",
    color: "#ede5d4",
    border: "#a89060",
    type: "rect"
  }, {
    label: "Completed",
    color: ODONTOGRAM_COLORS.completed,
    border: ODONTOGRAM_COLORS.completed,
    type: "circle"
  }, {
    label: "Planned",
    color: ODONTOGRAM_COLORS.planned,
    border: ODONTOGRAM_COLORS.planned,
    type: "circle"
  }, {
    label: "Caries",
    color: "transparent",
    border: ODONTOGRAM_COLORS.cariesOutline,
    type: "circle"
  }, {
    label: "Missing",
    color: ODONTOGRAM_COLORS.missingSilhouette,
    border: "#9ca3af",
    type: "rect"
  }, {
    label: "Crown",
    color: "#dbeafe",
    border: "#2563eb",
    type: "rect"
  }, {
    label: "Implant",
    color: "#ecfeff",
    border: "#06b6d4",
    type: "rect"
  }, {
    label: "Root Canal",
    color: "#fef9c3",
    border: "#d97706",
    type: "rect"
  }, {
    label: "Impacted",
    color: "#f3e8ff",
    border: "#a855f7",
    type: "rect"
  }, {
    label: "Unerupted",
    color: ODONTOGRAM_COLORS.uneruptedFill,
    border: ODONTOGRAM_COLORS.uneruptedStroke,
    type: "rect"
  }];
  return /*#__PURE__*/_jsx("div", {
    className: "flex flex-wrap justify-center gap-x-4 gap-y-1 px-4 py-2 border-t border-gray-100 dark:border-neutral-700/50",
    children: items.map(it => /*#__PURE__*/_jsxs("div", {
      className: "flex items-center gap-1.5 text-[10px] text-gray-500",
      children: [/*#__PURE__*/_jsx("span", {
        className: cn("w-2.5 h-2.5 border", it.type === "circle" ? "rounded-full" : "rounded-sm"),
        style: {
          background: it.color,
          borderColor: it.border
        }
      }), it.label]
    }, it.label))
  });
}

/* -- Main InteractiveToothChart -- */
/* -- Bridge Overlay -- */
function BridgeOverlay({
  connections,
  upper,
  nums,
  getCellWidth,
  upperAnchorHeight,
  layerVisibility
}) {
  if (layerVisibility?.bridges === false) return null;
  const indexMap = new Map();
  nums.forEach((n, i) => indexMap.set(n, i));
  const widths = nums.map(n => getCellWidth(n));
  const offsets = [0];
  for (let i = 0; i < widths.length; i += 1) {
    offsets.push(offsets[i] + widths[i]);
  }
  const centerByTooth = new Map();
  nums.forEach((n, i) => {
    centerByTooth.set(n, offsets[i] + widths[i] / 2);
  });
  const totalWidth = offsets[offsets.length - 1];
  const relevant = connections.filter(b => {
    const from = indexMap.get(b.fromTooth);
    const to = indexMap.get(b.toTooth);
    return from !== undefined && to !== undefined;
  });
  if (relevant.length === 0) return null;
  return /*#__PURE__*/_jsx("svg", {
    className: "absolute left-0 right-0 pointer-events-none",
    style: {
      top: upper ? upperAnchorHeight - 2 : 0,
      height: 12
    },
    width: "100%",
    viewBox: `0 0 ${Math.max(totalWidth, 1) + 2} 12`,
    preserveAspectRatio: "none",
    children: relevant.map(bridge => {
      const fromX = centerByTooth.get(bridge.fromTooth);
      const toX = centerByTooth.get(bridge.toTooth);
      const x1 = Math.min(fromX, toX);
      const x2 = Math.max(fromX, toX);
      const color = bridge.status === "completed" ? ODONTOGRAM_COLORS.completed : ODONTOGRAM_COLORS.planned;
      const dash = bridge.status === "planned" ? "4 2" : undefined;
      return /*#__PURE__*/_jsxs("g", {
        children: [/*#__PURE__*/_jsx("rect", {
          x: x1,
          y: 2,
          width: x2 - x1,
          height: 2.5,
          rx: 1,
          fill: color,
          strokeDasharray: dash,
          opacity: 0.8
        }), /*#__PURE__*/_jsx("rect", {
          x: x1,
          y: 5,
          width: x2 - x1,
          height: 2.5,
          rx: 1,
          fill: color,
          strokeDasharray: dash,
          opacity: 0.6
        }), /*#__PURE__*/_jsx("rect", {
          x: x1,
          y: 8,
          width: x2 - x1,
          height: 2.5,
          rx: 1,
          fill: color,
          strokeDasharray: dash,
          opacity: 0.4
        })]
      }, `bridge-${bridge.fromTooth}-${bridge.toTooth}`);
    })
  });
}
const InteractiveToothChart = ({
  teeth,
  selectedTooth: externalSelected,
  onSelectTooth,
  compact = false,
  expanded = false,
  selectedSurfaces: externalSurfaces,
  onSurfaceToggle: externalSurfaceToggle,
  enhancedTeeth,
  layerVisibility,
  bridgeConnections,
  hideDetailPanel = false,
  onDetailChange,
  highlightedTeeth,
  dentitionMode = "adult"
}) => {
  const [internalSelected, setInternalSelected] = useState(null);
  const [hoveredTooth, setHoveredTooth] = useState(null);
  const [contextMenu, setContextMenu] = useState(null);
  const [mousePos, setMousePos] = useState(null);
  const chartContainerRef = useRef(null);
  const handleMouseMove = useCallback(e => {
    if (chartContainerRef.current) {
      const rect = chartContainerRef.current.getBoundingClientRect();
      setMousePos({
        x: e.clientX - rect.left,
        y: e.clientY - rect.top
      });
    }
  }, []);
  const handleContextMenu = useCallback((e, toothNumber) => {
    setContextMenu({
      x: e.clientX,
      y: e.clientY,
      toothNumber
    });
  }, []);
  const selected = externalSelected !== undefined ? externalSelected : internalSelected;
  const handleSelect = useCallback(n => {
    if (onSelectTooth) onSelectTooth(n);else setInternalSelected(n);
  }, [onSelectTooth]);

  // Surface selection -- uses hook for internal state, or external controlled state
  const {
    selectedSurfaces,
    hoveredSurface,
    toggleSurface,
    hoverSurface
  } = useToothSurfaceSelection({
    selectedSurfaces: externalSurfaces,
    onSurfaceToggle: externalSurfaceToggle
  });

  // Ref-based stable callbacks -- identity never changes so React.memo on
  // ToothColumn can skip re-renders for teeth whose data hasn't changed.
  const toggleSurfaceRef = useRef(toggleSurface);
  toggleSurfaceRef.current = toggleSurface;
  const hoverSurfaceRef = useRef(hoverSurface);
  hoverSurfaceRef.current = hoverSurface;
  const stableToggleSurface = useCallback((toothNumber, surface) => {
    toggleSurfaceRef.current(toothNumber, surface);
  }, []);
  const stableHoverSurface = useCallback((toothNumber, surface) => {
    hoverSurfaceRef.current(toothNumber, surface);
  }, []);
  const teethMap = useMemo(() => {
    const map = new Map();
    teeth.forEach(t => map.set(t.toothNumber, t));
    return map;
  }, [teeth]);
  const defaultTooth = useMemo(() => {
    const cache = {};
    return n => {
      if (!cache[n]) {
        cache[n] = {
          toothNumber: n,
          condition: "present",
          surfaces: []
        };
      }
      return cache[n];
    };
  }, []);
  const baseCellWidth = expanded ? 104 : compact ? 68 : 84;

  /** Curve dual-view dimensions -- two stacked anatomical views per tooth */
  const primaryToothHeight = expanded ? 172 : compact ? 116 : 144;
  const secondaryToothHeight = expanded ? 124 : compact ? 84 : 104;
  const getCellWidth = useCallback(toothNumber => {
    const shape = getToothShape(toothNumber);
    if (shape === "molar") return baseCellWidth;
    return Math.round(baseCellWidth * 0.81);
  }, [baseCellWidth]);
  const activeTooth = teethMap.get(hoveredTooth ?? selected ?? -1) ?? null;
  const activeToothSurfaces = activeTooth ? selectedSurfaces[activeTooth.toothNumber] ?? [] : [];

  // Notify parent about active tooth + surface changes
  React.useEffect(() => {
    if (onDetailChange) {
      const clearFn = () => {
        if (activeTooth) {
          const surfs = selectedSurfaces[activeTooth.toothNumber] ?? [];
          surfs.forEach(s => toggleSurface(activeTooth.toothNumber, s));
        }
      };
      onDetailChange(activeTooth, activeToothSurfaces, clearFn);
    }
  }, [activeTooth, activeToothSurfaces.join(",")]); // eslint-disable-line react-hooks/exhaustive-deps

  const {
    upper: activeUpper,
    lower: activeLower
  } = useMemo(() => getTeethArrays(dentitionMode), [dentitionMode]);
  const renderArchRow = (nums, upper) => {
    const mid = Math.ceil(nums.length / 2);
    return /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsxs("div", {
        className: "flex relative",
        children: [nums.slice(0, mid).map(n => {
          const tooth = teethMap.get(n) ?? defaultTooth(n);
          const toothHovered = hoveredSurface?.toothNumber === n ? hoveredSurface.surface : null;
          const isPrimary = PRIMARY_SLOTS.has(n);
          return /*#__PURE__*/_jsx(ToothColumn, {
            tooth: tooth,
            upper: upper,
            hovered: hoveredTooth === n,
            selected: selected === n,
            cellWidth: getCellWidth(n),
            toothWidth: Math.max(48, getCellWidth(n) - 4),
            primaryToothHeight: primaryToothHeight,
            secondaryToothHeight: secondaryToothHeight,
            onHover: setHoveredTooth,
            onSelect: handleSelect,
            onContextMenu: handleContextMenu,
            selectedSurfaces: selectedSurfaces[n],
            onSurfaceToggle: stableToggleSurface,
            hoveredSurface: toothHovered,
            onSurfaceHover: stableHoverSurface,
            enhancedState: enhancedTeeth?.[n],
            layerVisibility: layerVisibility,
            dimmed: !!highlightedTeeth && highlightedTeeth.size > 0 && !highlightedTeeth.has(n),
            label: getToothLabel(n, dentitionMode),
            isPrimaryTooth: dentitionMode !== "adult" && isPrimary
          }, n);
        }), bridgeConnections && bridgeConnections.length > 0 && /*#__PURE__*/_jsx(BridgeOverlay, {
          connections: bridgeConnections,
          upper: upper,
          nums: nums.slice(0, mid),
          getCellWidth: getCellWidth,
          upperAnchorHeight: primaryToothHeight,
          layerVisibility: layerVisibility
        })]
      }), /*#__PURE__*/_jsx("div", {
        className: "w-px bg-gray-200 dark:bg-neutral-700 mx-0.5"
      }), /*#__PURE__*/_jsxs("div", {
        className: "flex relative",
        children: [nums.slice(mid).map(n => {
          const tooth = teethMap.get(n) ?? defaultTooth(n);
          const toothHovered = hoveredSurface?.toothNumber === n ? hoveredSurface.surface : null;
          const isPrimary = PRIMARY_SLOTS.has(n);
          return /*#__PURE__*/_jsx(ToothColumn, {
            tooth: tooth,
            upper: upper,
            hovered: hoveredTooth === n,
            selected: selected === n,
            cellWidth: getCellWidth(n),
            toothWidth: Math.max(48, getCellWidth(n) - 4),
            primaryToothHeight: primaryToothHeight,
            secondaryToothHeight: secondaryToothHeight,
            onHover: setHoveredTooth,
            onSelect: handleSelect,
            onContextMenu: handleContextMenu,
            selectedSurfaces: selectedSurfaces[n],
            onSurfaceToggle: stableToggleSurface,
            hoveredSurface: toothHovered,
            onSurfaceHover: stableHoverSurface,
            enhancedState: enhancedTeeth?.[n],
            layerVisibility: layerVisibility,
            dimmed: !!highlightedTeeth && highlightedTeeth.size > 0 && !highlightedTeeth.has(n),
            label: getToothLabel(n, dentitionMode),
            isPrimaryTooth: dentitionMode !== "adult" && isPrimary
          }, n);
        }), bridgeConnections && bridgeConnections.length > 0 && /*#__PURE__*/_jsx(BridgeOverlay, {
          connections: bridgeConnections,
          upper: upper,
          nums: nums.slice(mid),
          getCellWidth: getCellWidth,
          upperAnchorHeight: primaryToothHeight,
          layerVisibility: layerVisibility
        })]
      })]
    });
  };
  return /*#__PURE__*/_jsxs("div", {
    className: "flex flex-col gap-3",
    children: [/*#__PURE__*/_jsxs("div", {
      ref: chartContainerRef,
      className: "bg-gray-50 dark:bg-neutral-900 border border-gray-200 dark:border-neutral-700 rounded-xl overflow-hidden relative",
      onMouseMove: handleMouseMove,
      children: [/*#__PURE__*/_jsx("div", {
        className: "overflow-x-auto",
        children: /*#__PURE__*/_jsxs("div", {
          className: "min-w-fit px-3 py-3",
          children: [/*#__PURE__*/_jsx("div", {
            className: "flex justify-center",
            children: renderArchRow(activeUpper, true)
          }), /*#__PURE__*/_jsx(NumberStrip, {
            upper: activeUpper,
            lower: activeLower,
            teethMap: teethMap,
            hoveredTooth: hoveredTooth,
            selectedTooth: selected,
            getCellWidth: getCellWidth,
            expanded: expanded,
            onHover: setHoveredTooth,
            onSelect: handleSelect,
            dentitionMode: dentitionMode
          }), /*#__PURE__*/_jsx("div", {
            className: "flex justify-center",
            children: renderArchRow(activeLower, false)
          })]
        })
      }), /*#__PURE__*/_jsx(ChartLegend, {}), hoveredSurface && mousePos && /*#__PURE__*/_jsxs("div", {
        className: "absolute z-50 pointer-events-none px-2 py-1 rounded-md bg-gray-900 text-white text-[11px] font-medium shadow-lg whitespace-nowrap",
        style: {
          left: mousePos.x + 12,
          top: mousePos.y - 28
        },
        children: [SURFACE_LABELS[hoveredSurface.surface] ?? hoveredSurface.surface, /*#__PURE__*/_jsxs("span", {
          className: "text-gray-400 ml-1",
          children: ["(#", hoveredSurface.toothNumber, ")"]
        })]
      })]
    }), !hideDetailPanel && activeTooth && /*#__PURE__*/_jsxs("div", {
      className: "bg-white dark:bg-neutral-900 border border-gray-200 dark:border-neutral-700 rounded-xl",
      children: [/*#__PURE__*/_jsx("div", {
        className: "px-3 py-2 border-b border-gray-100 dark:border-neutral-700/50 text-[10px] font-bold text-gray-500 dark:text-neutral-400 uppercase tracking-wider",
        children: "Tooth Detail"
      }), /*#__PURE__*/_jsx(ToothDetailPanel, {
        tooth: activeTooth,
        selectedSurfaces: activeTooth ? selectedSurfaces[activeTooth.toothNumber] : undefined,
        onClearSurfaces: () => {
          if (activeTooth) {
            const currentSurfaces = selectedSurfaces[activeTooth.toothNumber] ?? [];
            currentSurfaces.forEach(s => toggleSurface(activeTooth.toothNumber, s));
          }
        }
      })]
    }), contextMenu && /*#__PURE__*/_jsx(SurfaceContextMenu, {
      x: contextMenu.x,
      y: contextMenu.y,
      toothNumber: contextMenu.toothNumber,
      selectedSurfaces: selectedSurfaces[contextMenu.toothNumber] ?? [],
      onToggleSurface: surface => toggleSurface(contextMenu.toothNumber, surface),
      onClose: () => setContextMenu(null)
    })]
  });
};

/* -- Surface Context Menu (right-click) -- */
function SurfaceContextMenu({
  x,
  y,
  toothNumber,
  selectedSurfaces,
  onToggleSurface,
  onClose
}) {
  const surfaces = [{
    key: "M",
    label: "Mesial"
  }, {
    key: "O",
    label: "Occlusal"
  }, {
    key: "D",
    label: "Distal"
  }, {
    key: "B",
    label: "Buccal"
  }, {
    key: "L",
    label: "Lingual"
  }, {
    key: "I",
    label: "Incisal"
  }];
  React.useEffect(() => {
    const handler = () => onClose();
    window.addEventListener("click", handler);
    return () => window.removeEventListener("click", handler);
  }, [onClose]);
  return /*#__PURE__*/_jsxs("div", {
    className: "fixed z-50 bg-white dark:bg-neutral-800 border border-gray-200 dark:border-neutral-700 rounded-lg shadow-xl py-1 min-w-[160px]",
    style: {
      left: x,
      top: y
    },
    onClick: e => e.stopPropagation(),
    children: [/*#__PURE__*/_jsxs("div", {
      className: "px-3 py-1.5 text-[10px] font-bold text-gray-400 uppercase tracking-wider border-b border-gray-100 dark:border-neutral-700/50",
      children: ["Tooth #", toothNumber, " \u2014 Surfaces"]
    }), surfaces.map(({
      key,
      label
    }) => {
      const active = selectedSurfaces.includes(key);
      return /*#__PURE__*/_jsxs("button", {
        type: "button",
        onClick: () => onToggleSurface(key),
        className: cn("w-full flex items-center gap-2 px-3 py-1.5 text-xs hover:bg-gray-50 dark:hover:bg-neutral-700 transition-colors", active ? "text-blue-700 font-semibold" : "text-gray-700 dark:text-neutral-300"),
        children: [/*#__PURE__*/_jsx("span", {
          className: cn("w-4 h-4 rounded border-2 flex items-center justify-center text-[10px]", active ? "border-blue-600 bg-blue-600 text-white" : "border-gray-300 dark:border-neutral-600"),
          children: active ? "\u2713" : ""
        }), /*#__PURE__*/_jsx("span", {
          className: "font-mono text-[11px] w-4",
          children: key
        }), /*#__PURE__*/_jsx("span", {
          children: label
        })]
      }, key);
    })]
  });
}
export default InteractiveToothChart;
export { ToothDetailPanel };